import { lazy, memo, Suspense, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { HeroV2Config } from "@/types/heroBannerV2";

const HeroBannerV2 = lazy(() => import("@/components/HeroBannerV2"));

export { FONT_OPTIONS } from "./hero/types";

const HERO_CACHE_KEY = "hero_v2_config_cache_v1";
const HERO_ENABLED_FLAG_KEY = "hero_v2_enabled_flag_v1";
const HERO_CACHE_TTL_MS = 5 * 60 * 1000;
const HERO_FLAG_TTL_MS = 60 * 60 * 1000; // 1 hour — enabled flag rarely changes

const HeroSkeleton = ({ aspectRatio }: { aspectRatio?: string }) => (
  <section
    className="relative w-full overflow-hidden"
    style={{
      aspectRatio: aspectRatio || "16 / 9",
      contain: "layout style paint",
      background: "hsl(var(--background))",
    }}
  >
    <div
      className="absolute inset-0"
      style={{
        background: "linear-gradient(90deg, transparent 0%, hsl(var(--muted) / 0.3) 50%, transparent 100%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
    <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
  </section>
);

const getCachedAspectRatio = (cfg: HeroV2Config | null): string | undefined => {
  if (!cfg) return undefined;
  const w = (cfg as any)?.section?.width || (cfg as any)?.canvas?.width;
  const h = (cfg as any)?.section?.height || (cfg as any)?.canvas?.height;
  if (w && h) return `${w} / ${h}`;
  return undefined;
};

const readCachedConfig = (): { config: HeroV2Config | null; fresh: boolean } => {
  if (typeof window === "undefined") return { config: null, fresh: false };
  try {
    const raw = window.localStorage.getItem(HERO_CACHE_KEY);
    if (!raw) return { config: null, fresh: false };
    const parsed = JSON.parse(raw) as { value: string; fetchedAt: number };
    const fresh = Date.now() - parsed.fetchedAt < HERO_CACHE_TTL_MS;
    const cfg = JSON.parse(parsed.value);
    return { config: cfg?.enabled ? cfg : null, fresh };
  } catch {
    return { config: null, fresh: false };
  }
};

const writeCachedConfig = (value: string) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(HERO_CACHE_KEY, JSON.stringify({ value, fetchedAt: Date.now() }));
  } catch {}
};

const readEnabledFlag = (): { enabled: boolean | null; fresh: boolean } => {
  if (typeof window === "undefined") return { enabled: null, fresh: false };
  try {
    const raw = window.localStorage.getItem(HERO_ENABLED_FLAG_KEY);
    if (!raw) return { enabled: null, fresh: false };
    const parsed = JSON.parse(raw) as { enabled: boolean; fetchedAt: number };
    return { enabled: parsed.enabled, fresh: Date.now() - parsed.fetchedAt < HERO_FLAG_TTL_MS };
  } catch {
    return { enabled: null, fresh: false };
  }
};

const writeEnabledFlag = (enabled: boolean) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(HERO_ENABLED_FLAG_KEY, JSON.stringify({ enabled, fetchedAt: Date.now() }));
  } catch {}
};

const HeroBanner = memo(() => {
  const initialFlag = useMemo(() => readEnabledFlag(), []);
  const initial = useMemo(() => readCachedConfig(), []);
  // Only trust cached config if it's still fresh — otherwise wait for fetch
  // to avoid flashing a stale/old hero banner before the new one loads.
  const [v2Config, setV2Config] = useState<HeroV2Config | null>(initial.fresh ? initial.config : null);
  // If we know hero is disabled (cached flag), don't show skeleton at all
  const [loading, setLoading] = useState(
    initialFlag.enabled === false ? false : !(initial.fresh && initial.config)
  );
  const [knownDisabled, setKnownDisabled] = useState(initialFlag.enabled === false && initialFlag.fresh);

  useEffect(() => {
    // Skip refetch if cache is fresh
    if (initial.fresh) return;
    // Skip if we know it's disabled (cached flag still fresh)
    if (knownDisabled) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("site_settings")
          .select("value")
          .eq("key", "hero_banner_v2_config")
          .maybeSingle();

        if (cancelled) return;

        const raw = data?.value;
        if (!raw) {
          writeEnabledFlag(false);
          setV2Config(null);
          setLoading(false);
          return;
        }

        const parsed = JSON.parse(raw);
        const enabled = !!parsed?.enabled;
        writeEnabledFlag(enabled);

        if (enabled) {
          writeCachedConfig(raw);
          setV2Config(parsed);
        } else {
          setV2Config(null);
        }
      } catch {
        if (!cancelled) setV2Config(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [initial.fresh, knownDisabled]);

  // If cached flag says disabled — render nothing immediately, no skeleton, no fetch delay
  if (knownDisabled && !v2Config) {
    return null;
  }

  const aspectRatio = getCachedAspectRatio(v2Config);

  if (loading && !v2Config) {
    return <HeroSkeleton aspectRatio={aspectRatio} />;
  }

  if (!v2Config) {
    return null;
  }

  return (
    <Suspense fallback={<HeroSkeleton aspectRatio={aspectRatio} />}>
      <HeroBannerV2 initialConfig={v2Config} />
    </Suspense>
  );
});

HeroBanner.displayName = "HeroBanner";

export default HeroBanner;
